<template>
    <v-container>
        <v-row justify="center">
            <v-col cols="12" md="8">
                <v-card class="pa-4" elevation="4">
                    <v-card-title>
                        {{ isEditing ? 'Editar Usuário' : 'Novo Usuário' }}
                    </v-card-title>

                    <v-card-text>
                        <v-tabs v-model="currentTab" dark class="mb-6">
                            <v-tab value="details">Dados de Acesso</v-tab>
                        </v-tabs>

                        <v-form ref="formRef" @submit.prevent="handleSubmit">
                            <v-window v-model="currentTab" class="mt-2">
                                <v-window-item value="details">
                                    <v-row class="mt-2">
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formValue.name" label="Nome Completo"
                                                :rules="[rules.name]" />
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formValue.email" label="Email (Login)"
                                                :rules="[rules.email]" :disabled="isEditing" />
                                        </v-col>

                                        <v-col v-if="!isEditing" cols="12" sm="6">
                                            <v-text-field v-model="formValue.password" type="password" label="Senha"
                                                :rules="isEditing ? [] : [rules.password]"
                                                :placeholder="isEditing ? 'Deixe em branco para não alterar' : 'Digite a senha'" />
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formValue.phone" v-maska="'(##) #####-####'"
                                                label="Telefone" />
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-select v-model="formValue.status" :items="statusOptions"
                                                item-title="label" item-value="value" label="Status"
                                                :rules="[rules.status]" />
                                        </v-col>

                                        <v-col cols="12" md="12">
                                            <v-autocomplete v-model="formValue.permissions"
                                                :items="availablePermissions" item-title="description" item-value="id"
                                                label="Permissões" multiple chips />
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-switch v-model="formValue.is_active" color="success" label="Ativo?" />
                                        </v-col>
                                    </v-row>
                                </v-window-item>
                            </v-window>
                        </v-form>
                    </v-card-text>

                    <v-card-actions class="d-flex justify-space-between">
                        <v-btn color="secondary" variant="flat" @click="handleCancel">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Voltar
                        </v-btn>
                        <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
                            @click="handleSubmit">
                            <v-icon left>mdi-content-save</v-icon>
                            {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Usuário' }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vMaska } from "maska/vue";
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingStore } from '@/store/loadingStore';

const toastStore = useToastStore();
const loadingStore = useLoadingStore();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);
const availablePermissions = ref([]);

const isLoading = ref(false);
const isSubmitting = ref(false);

const currentTab = ref('details');

const isEditing = computed(() => !!route.params.id);

const formValue = ref({
    name: '',
    email: '',
    password: '',
    role: 'administrative',
    phone: '',
    enterprise_id: null,
    is_active: true,
    status: 'pending',
});

const statusOptions = [
    { label: 'Ativado', value: 'activated' },
    { label: 'Pendente', value: 'pending' },
    { label: 'Cancelado', value: 'canceled' },
];

const rules = {
    name: v => !!v || 'O nome é obrigatório.',
    email: v => !!v && /.+@.+\..+/.test(v) || 'O email deve ser válido e é obrigatório.',
    password: v => !!v || 'A senha é obrigatória para novos usuários.',
    role: v => !!v || 'O perfil de acesso é obrigatório.',
    status: v => !!v || 'O status é obrigatório.',
};

const loadPermission = async (id) => {
    isLoading.value = true;
    loadingStore.show();
    try {
        const response = await api.get(`/permissions`);

        availablePermissions.value = response.data;

    } catch (error) {
        console.error('Erro ao carregar permissões:', error);
        toastStore.showToast({
            message: error?.response?.data?.error || 'Erro ao carregar permissões',
            color: 'error',
        });
    } finally {
        isLoading.value = false;
        loadingStore.hide();
    }
};

const loadUser = async (id) => {
    isLoading.value = true;
    loadingStore.show();
    try {
        const response = await api.get(`/users/${id}`);
        const data = response.data.data;

        if (!data) {
            toastStore.showToast({ message: 'O usuário não foi encontrado.', color: 'warning' });
            return;
        }

        formValue.value = {
            ...formValue.value,
            ...data,
            is_active: data.is_active === 1 || data.is_active === true,
            permissions: data.permissions.map(p => p.id),
            password: '',
        };

    } catch (error) {
        console.error('Erro ao carregar os dados do usuário:', error);
        toastStore.showToast({ message: error?.response?.data?.error || 'Erro ao carregar os dados do usuário.', color: 'error' });
    } finally {
        isLoading.value = false;
        loadingStore.hide();
    }
};

const handleSubmit = async () => {
    const { valid } = await formRef.value.validate();
    if (!valid) {
        currentTab.value = 'details';
        return;
    }

    isSubmitting.value = true;
    const payload = { ...formValue.value };

    if (isEditing.value && !payload.password) {
        delete payload.password;
    } else if (!isEditing.value && payload.password) {
    } else if (!isEditing.value && !payload.password) {
        currentTab.value = 'details';
        isSubmitting.value = false;
        return;
    }

    if (payload.phone) payload.phone = String(payload.phone).replace(/\D/g, '');

    payload.is_active = payload.is_active ? 1 : 0;

    try {
        let response;

        if (isEditing.value) {
            response = await api.put(`/users/${route.params.id}`, payload);
        } else {
            payload.role = 'administrative';
            response = await api.post('/user/admin', payload);
        }

        router.push('/app/usuarios');
        toastStore.showToast({
            message: isEditing.value ? 'Usuário atualizado com sucesso!' : 'Usuário cadastrado com sucesso!',
            color: 'success',
        });
    } catch (error) {
        console.error('Erro ao salvar usuário:', error);
        const errorMessage = error?.response?.data?.error || 'Erro ao salvar usuário.';
        toastStore.showToast({ message: errorMessage, color: 'error' });
    } finally {
        isSubmitting.value = false;
    }
};

const handleCancel = () => { router.push('/app/usuarios'); };

onMounted(async () => {
    await loadPermission();

    if (isEditing.value) {
        await loadUser(route.params.id);
    }
});
</script>

<style scoped>
/* Estilos mantidos do modelo */
</style>